import type React from "react";
import { useEffect, useRef, useState } from "react";

import { colorsSelector } from "@redux/colorPalette/selectors";
import {
	configureVolumeMuteState,
	useMutedVolumeSelector,
	setVolumeBarDisplay,
} from "@redux/player";
import { useVolumeBarDisplaySelector } from "@redux/player";
import { useDispatch, useSelector } from "react-redux";

import { sendMultiScreenEventToTv } from "services/helpers/multiScreen";
import type ColormapInterface from "shared-types/colormapinterface";
import { PlayerEvent } from "shared-types/player-event";

import {
	SliderContainer,
	VolumeBarContainer,
	VolumeBarVolumeUpIcon,
	VolumeBarVolumeOffIcon,
	VolumeContainer,
	ControlBarVolumeUpIcon,
	ControlBarVolumeOffIcon,
	VolumeIconContainer,
	VolumeIconSubContainer,
	VolumeBarIconContainer,
	VolumeSlider,
} from "./Volume.style";

interface VolumeIconProps {
	muted: boolean;
	colors: ColormapInterface;
}
const VolumeBarIconComponent: React.FC<VolumeIconProps> = ({
	muted,
	colors,
}) => {
	return muted ? (
		<VolumeBarVolumeOffIcon {...{ colors }} />
	) : (
		<VolumeBarVolumeUpIcon {...{ colors }} />
	);
};

const VolumeBarComponent: React.FC<VolumeBarProps> = ({ on }) => {
	const dispatch = useDispatch();
	const muted = useMutedVolumeSelector();
	const colors = useSelector(colorsSelector);
	const mounted = useRef(false);

	const VOLUME_SLIDER_MAX = 100;

	const updateVolumeBar = (value: number | number[]) => {
		setValue(value as number);
	};

	const sendVolumeUpdateEvent = (value: string) => {
		sendMultiScreenEventToTv(
			PlayerEvent.UPDATE_VOLUME_LEVEL,
			value,
			Date.now(),
		);
	};

	const [value, setValue] = useState<number | number[]>(VOLUME_SLIDER_MAX);

	useEffect(() => {
		if (!mounted.current) {
			mounted.current = true;
			sendVolumeUpdateEvent(VOLUME_SLIDER_MAX.toString());
		}

		if (value === 0) {
			dispatch(configureVolumeMuteState(true));
		} else if (muted) {
			dispatch(configureVolumeMuteState(false));
		}
	}, [value, mounted.current]);

	return (
		<VolumeBarContainer $on={on} {...{ colors }}>
			<VolumeBarIconContainer>
				<VolumeBarIconComponent muted={muted} {...{ colors }} />
			</VolumeBarIconContainer>
			<SliderContainer>
				<VolumeSlider
					colors={colors}
					max={VOLUME_SLIDER_MAX}
					value={value}
					onChange={(_, newValue) => {
						updateVolumeBar(newValue);
						sendVolumeUpdateEvent(newValue.toString());
					}}
				/>
			</SliderContainer>
		</VolumeBarContainer>
	);
};

const ControlBarVolumeIcon: React.FC<VolumeIconProps> = ({ muted }) => {
	const dispatch = useDispatch();
	const volumeBarDisplayed = useVolumeBarDisplaySelector();

	const updateVolumeBarDisplay = () => {
		dispatch(setVolumeBarDisplay(!volumeBarDisplayed));
	};

	const volumeBarLogoProps = { onClick: () => updateVolumeBarDisplay() };

	return muted ? (
		<ControlBarVolumeOffIcon {...volumeBarLogoProps} />
	) : (
		<ControlBarVolumeUpIcon {...volumeBarLogoProps} />
	);
};

const VolumeComponent: React.FC = () => {
	const volumeBarDisplayed = useVolumeBarDisplaySelector();
	const muted = useMutedVolumeSelector();
	const colors = useSelector(colorsSelector);
	return (
		<VolumeContainer>
			<VolumeBarComponent on={volumeBarDisplayed} />
			<VolumeIconContainer>
				<VolumeIconSubContainer>
					<ControlBarVolumeIcon muted={muted} {...{ colors }} />
				</VolumeIconSubContainer>
			</VolumeIconContainer>
		</VolumeContainer>
	);
};

interface VolumeBarProps {
	on: boolean;
}

export default VolumeComponent;
